import { GridColDef } from "@mui/x-data-grid";
import ContactsIcon from "@mui/icons-material/Contacts";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService, { MainApi } from "../../../../services/mainApi.service";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  Table,
  useFetch,
  UsePrivileges,
} from "@4uhub/lib4uhub";
import { DownloadModelButton } from "./DownloadModelButton/DownloadModelButton";
import { useCallback, useEffect, useMemo, useState } from "react";
import SyncIcon from "@mui/icons-material/Sync";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import Verified from "@mui/icons-material/Verified";
import { IListImportBatch } from "../../../../models/list-import-batch";
import { ListImportBatchService } from "../../../../services/listImportBatch.service";
import { Box } from "@mui/material";

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";

const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const listImportBatch = new ListImportBatchService();

const LIST_IMPORT_BATCH_ROUTE =
  process.env.REACT_APP_MAIN + "/api/v1/ListImportBatch/";
const listImportBatchService = new MainApiService<IListImportBatch>(
  LIST_IMPORT_BATCH_ROUTE,
);

const translationPath = "page.4uDlist.listFiles.";

const ListFilesPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { sendRequest } = useFetch(genericService.list);

  const { sendRequest: define } = useFetch(listImportBatch.define);

  const { privileges } = UsePrivileges();

  const [listImportBatchOriginOptions, setListImportBatchOriginOptions] =
    useState<ISelectType[]>([]);

  const [listImportBatchOriginSelected, setListImportBatchOriginSelected] =
    useState<ISelectType | null>(null);

  const [refresh, setRefresh] = useState(0);

  const permissionChangeStatusApi = privileges.some(
    (priv) =>
      priv.domainName === "all" || priv.domainName === "listimportbatchdefine",
  );

  const permissionAddToList = privileges.some(
    (priv) =>
      priv.domainName === "all" ||
      priv.domainName === "listimportbatchaddtolist",
  );

  const columns: GridColDef<IListImportBatch>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "code",
      headerName: t(translationPath + "code") || "Code",
      maxWidth: 80,
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "listImportBatchStatus",
      headerName: t(translationPath + "import_status") || "Import Status",
      maxWidth: 180,
      renderCell: ({ row: { listImportBatchStatus } }) => {
        return listImportBatchStatus.name.length > 0 ? (
          <OverflowTooltip
            text={listImportBatchStatus.name}
            tooltip={listImportBatchStatus.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "listClassification",
      headerName:
        t(translationPath + "list_classification") || "List Classification",
      maxWidth: 170,
      renderCell: ({ row: { listClassification } }) => {
        return listClassification && listClassification.name.length > 0 ? (
          <OverflowTooltip
            text={listClassification.name}
            tooltip={listClassification.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "integrationType",
      headerName: t(translationPath + "integration_type") || "Integration Type",
      maxWidth: 220,
      renderCell: ({ row: { integrationType } }) => {
        return integrationType && integrationType.name.length > 0 ? (
          <OverflowTooltip
            text={integrationType.name}
            tooltip={integrationType.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "listImportBatchOrigin",
      headerName: t(translationPath + "import_origin") || "List Origin",
      maxWidth: 120,
      renderCell: ({ row: { listImportBatchOrigin } }) => {
        return listImportBatchOrigin &&
          listImportBatchOrigin.name.length > 0 ? (
          <OverflowTooltip
            text={listImportBatchOrigin.name}
            tooltip={listImportBatchOrigin.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "listImportApiStatus",
      headerName: t(translationPath + "import_api_status") || "Api Status",
      maxWidth: 90,
      renderCell: ({ row: { listImportApiStatus } }) => {
        return listImportApiStatus && listImportApiStatus.name.length > 0 ? (
          <OverflowTooltip
            text={listImportApiStatus.name}
            tooltip={listImportApiStatus.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        );
      },
    },
    {
      field: "qtyContactsTotal",
      headerName: t(translationPath + "total") || "Total",
      maxWidth: 85,
      renderCell: ({ row: { qtyContactsTotal } }) => (
        <OverflowTooltip
          text={String(qtyContactsTotal)}
          tooltip={String(qtyContactsTotal)}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "qtyContactsError",
      headerName: t(translationPath + "with_error") || "With Error",
      maxWidth: 85,
      renderCell: ({ row: { qtyContactsError } }) => (
        <OverflowTooltip
          text={String(qtyContactsError)}
          tooltip={String(qtyContactsError)}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "qtyContactsWarning",
      headerName: t(translationPath + "with_alert") || "With Alert",
      maxWidth: 85,
      renderCell: ({ row: { qtyContactsWarning } }) => (
        <OverflowTooltip
          text={String(qtyContactsWarning)}
          tooltip={String(qtyContactsWarning)}
          textTransform="none"
          variant="body2"
        />
      ),
    },
    {
      field: "qtyContactsConverted",
      headerName: t(translationPath + "converted") || "Converted",
      maxWidth: 85,
      renderCell: ({ row: { qtyContactsConverted } }) => {
        return (
          <OverflowTooltip
            text={String(qtyContactsConverted)}
            tooltip={String(qtyContactsConverted)}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const fetchTicketServiceOptions = useCallback(async () => {
    const { data, success } = await sendRequest({
      Identifier: "ListImportBatchOrigin",
    });
    if (data && success) {
      setListImportBatchOriginOptions(data);
    }
  }, [sendRequest]);

  const handleAdd = () => {
    navigate("new");
  };

  const handleOpenListContacts = useCallback(
    (id: string) => {
      navigate(`listContacts/${id}`);
    },
    [navigate],
  );

  const handleConvertToNewList = useCallback(
    (id: string) => {
      navigate(`${id}/convertToNewList`);
    },
    [navigate],
  );

  const handleAddToExistingList = useCallback(
    (id: string) => {
      navigate(`${id}/addToList`);
    },
    [navigate],
  );

  const handleChangeStatusApi = useCallback(
    async (id: string) => {
      const { success } = await define(id);
      if (success) {
        setRefresh((oldState) => (oldState += 1));
      }
    },
    [define],
  );

  const props = useMemo(() => {
    return {
      refresh,
      ListImportBatchOriginId: listImportBatchOriginSelected?.id,
    };
  }, [refresh, listImportBatchOriginSelected]);

  useEffect(() => {
    fetchTicketServiceOptions();
  }, [fetchTicketServiceOptions]);

  return (
    <>
      <Table<IListImportBatch>
        showDefaultMacroFilters={false}
        service={listImportBatchService}
        serviceProps={props}
        columns={columns}
        title={t(translationPath + "title")}
        searchInput
        onAdd={handleAdd}
        showDeleteButton={false}
        rowActions={(params) => {
          const actions = [];

          if (params.row.id) {
            actions.push({
              action: () => handleOpenListContacts(params.row.id),
              icon: <ContactsIcon fontSize="small" />,
              text: t(translationPath + "list_contacts"),
            });
            if (
              params.row.listImportBatchStatus.code === "4" ||
              params.row.listImportBatchStatus.code === "5"
            ) {
              actions.push({
                action: () => handleConvertToNewList(params.row.id),
                icon: <SyncIcon fontSize="small" />,
                text: t(translationPath + "convertToList.convert_to_new_list"),
              });
              if (permissionAddToList) {
                actions.push({
                  action: () => handleAddToExistingList(params.row.id),
                  icon: <PlaylistAddIcon fontSize="small" />,
                  text: t(
                    translationPath + "convertToList.add_to_existing_list",
                  ),
                });
              }
            }
            if (
              permissionChangeStatusApi &&
              params.row.listImportApiStatus.code === "1"
            ) {
              actions.push({
                action: () => handleChangeStatusApi(params.row.id),
                icon: <Verified fontSize="small" />,
                text: t(translationPath + "define"),
              });
            }
          }

          return actions;
        }}
        macroFilters={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
            }}
          >
            <AutoCompleteUncontrolled
              label={t(translationPath + "import_origin") || "List Origin"}
              onValueSelected={setListImportBatchOriginSelected}
              value={listImportBatchOriginSelected}
              options={listImportBatchOriginOptions}
              clearable={true}
              size="small"
              fullWidth
              sx={{ minWidth: 250 }}
            />
            <DownloadModelButton />
          </Box>
        )}
        searchInputPlaceHolder={
          t(translationPath + "search") || "Search for a list classification"
        }
        addButtonLabel={t(translationPath + "add") || "Add List Classification"}
        confirmOptions={{
          title: t(translationPath + "confirm.title"),
          description: t(translationPath + "confirm.description"),
          confirmationButtonProps: {
            variant: "contained",
          },
        }}
      />
    </>
  );
};

export default ListFilesPage;
